import React from 'react';
import { Form, FormikConfig } from 'formik';

import { Button, Input, InputField } from '../../components';
import { FormContextProvider } from '../../context';
import { AuthHeader } from '../auth-header';
import { usePasswordValidationSchema } from './change-password-form.hook';
import { PasswordStrengthIndicator } from './password-strength-indicator.component';

// source: https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
const passwordRequirements: { regexp: RegExp; description: string }[] = [
  { regexp: /[a-z]/, description: 'Password must contain a lower case letter' },
  {
    regexp: /[A-Z]/,
    description: 'Password must contain an upper case letter',
  },
  {
    regexp: /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`]/,
    description: 'Password must contain a special character',
  },
  { regexp: /\d/, description: 'Password must contain a number' },
  {
    regexp: /.{8,}/,
    description: 'Password must contain at least 8 characters',
  },
];

export type ChangePasswordFormValues = {
  oldPassword: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
};

export type ChangePasswordFormProps = {
  showOldPasswordField?: boolean;
  showCodeField?: boolean;
  showBackButton?: boolean;
  backButtonText?: string;
  isPending?: boolean;
  email: string;
  helpText?: string;
  onChange?: () => void;
  onSubmit: FormikConfig<ChangePasswordFormValues>['onSubmit'];
  onBack?: () => void;
};

export const ChangePasswordForm = ({
  isPending,
  showOldPasswordField,
  showCodeField,
  showBackButton,
  backButtonText,
  email,
  helpText,
  onChange,
  onSubmit,
  onBack,
}: ChangePasswordFormProps) => {
  const validationSchema = usePasswordValidationSchema(
    passwordRequirements,
    showOldPasswordField,
    showCodeField
  );

  return (
    <FormContextProvider<ChangePasswordFormValues>
      initialValues={{
        oldPassword: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
      }}
      validationSchema={validationSchema}
      onChange={onChange}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values, dirty }) => (
        <Form>
          <AuthHeader
            heading="Choose a new password"
            helpText={
              helpText ??
              'Choose a strong password that is a mix of A-Z, 0-9 and special characters.'
            }
          />
          <div className="space-y-4 mb-8">
            {/* Hidden input that helps password managers */}
            <Input
              autoComplete="username"
              defaultValue={email}
              className="hidden"
            />
            {showOldPasswordField && (
              <InputField
                name="oldPassword"
                label="Old password"
                showRequiredIndicator={false}
              >
                {(field) => (
                  <Input
                    type="password"
                    autoComplete="current-password"
                    {...field}
                  />
                )}
              </InputField>
            )}
            {showCodeField && (
              <InputField name="code" showRequiredIndicator={false}>
                {(field) => (
                  <Input
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    {...field}
                  />
                )}
              </InputField>
            )}
            <InputField
              name="newPassword"
              label="New password"
              showRequiredIndicator={false}
            >
              {(field) => (
                <Input type="password" autoComplete="new-password" {...field} />
              )}
            </InputField>
            <InputField
              name="confirmPassword"
              label="Confirm new password"
              showRequiredIndicator={false}
            >
              {(field) => (
                <Input type="password" autoComplete="new-password" {...field} />
              )}
            </InputField>
            {dirty && (
              <PasswordStrengthIndicator
                value={values.newPassword}
                passwordRequirements={passwordRequirements}
              />
            )}
          </div>
          <div className="flex justify-end space-x-2">
            {showBackButton && (
              <Button variant="outline" onClick={onBack}>
                {backButtonText ?? 'Back to login'}
              </Button>
            )}
            <Button type="submit" isPending={isPending ?? isSubmitting}>
              Change password
            </Button>
          </div>
        </Form>
      )}
    </FormContextProvider>
  );
};
